import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Loader2, Star, CreditCard, Clock, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d; }
};

const MyCampsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCamps();
  }, []);

  const fetchCamps = async () => {
    try {
      const res = await api.get('/bootcamps/bookings/my');
      setBookings(res.data?.bookings || []);
    } catch (err) {
      console.error('Failed to load camp bookings', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="py-12 text-center">
          <Star className="w-12 h-12 mx-auto text-white/20 mb-3" />
          <p className="text-white/60">No camp bookings yet.</p>
          <p className="text-white/40 text-sm mt-1">
            Visit the <Link to="/bootcamps" className="text-purple-400 hover:underline">Bootcamps page</Link> to see what's coming up.
          </p>
        </CardContent>
      </Card>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      {bookings.map((b) => {
        const plan = b.installment_plan;
        const installments = plan?.installments || [];
        const pendingInstalments = installments.filter((i) => i.status !== 'paid');
        const overdueCount = pendingInstalments.filter((i) => (i.due_date || '') < today).length;
        const totalPaid = installments.filter((i) => i.status === 'paid').reduce((s, i) => s + Number(i.amount || 0), 0);

        return (
          <Card key={b.id} className="bg-[#111] border-[#333]" data-testid={`camp-card-${b.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Star className="w-4 h-4 text-purple-400" />
                    {b.bootcamp_name}
                  </CardTitle>
                  <p className="text-white/60 text-sm mt-1">
                    {b.student_name} {b.student_age ? `(age ${b.student_age})` : ''}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {b.payment_status === 'paid' && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" /> Paid
                    </Badge>
                  )}
                  {b.payment_status === 'installments' && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Clock className="w-3 h-3 mr-1" /> Paying by instalments
                    </Badge>
                  )}
                  {b.payment_status === 'partial' && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      Part paid
                    </Badge>
                  )}
                  {b.payment_status === 'pending' && (
                    <Badge className="bg-pink-500/20 text-pink-400 border-pink-500/30">
                      Awaiting payment
                    </Badge>
                  )}
                  {overdueCount > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <AlertTriangle className="w-3 h-3 mr-1" /> {overdueCount} overdue
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/70">
                {(b.start_date || b.bootcamp_start_date) && (
                  <span><Calendar className="w-3 h-3 inline mr-1" /> {formatDate(b.start_date || b.bootcamp_start_date)}</span>
                )}
                <span><CreditCard className="w-3 h-3 inline mr-1" /> Total £{Number(b.amount || 0).toFixed(2)}</span>
                {b.sibling_discount_info && (
                  <span className="text-pink-300">30% sibling discount applied</span>
                )}
              </div>

              {/* Pending payment — CTA to pay now */}
              {b.payment_status === 'pending' && (
                <Link
                  to={`/bootcamp-pay/${b.id}`}
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm"
                  data-testid={`pay-now-${b.id}`}
                >
                  Pay now <ExternalLink className="w-3 h-3" />
                </Link>
              )}

              {/* Instalment schedule */}
              {plan && installments.length > 0 && (
                <div className="bg-[#0a0a0a] border border-[#222] rounded p-3 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white text-sm font-semibold">Instalment schedule</h4>
                    <span className="text-xs text-white/60">
                      Paid £{totalPaid.toFixed(2)} of £{Number(plan.total_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {installments.map((inst, i) => {
                      const isOverdue = inst.status !== 'paid' && (inst.due_date || '') < today;
                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between text-sm"
                          data-testid={`installment-${b.id}-${i}`}
                        >
                          <div className="flex items-center gap-2">
                            {inst.status === 'paid' ? (
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            ) : isOverdue ? (
                              <AlertTriangle className="w-4 h-4 text-red-400" />
                            ) : (
                              <Clock className="w-4 h-4 text-white/40" />
                            )}
                            <span className={inst.status === 'paid' ? 'text-white/60' : isOverdue ? 'text-red-400' : 'text-white'}>
                              {formatDate(inst.due_date)}
                            </span>
                          </div>
                          <span className={`font-medium ${inst.status === 'paid' ? 'text-white/60' : 'text-white'}`}>
                            £{Number(inst.amount || 0).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MyCampsTab;
